import React, { memo, useCallback, useEffect, useRef } from 'react'
import { Box, makeStyles, useMediaQuery, useTheme } from '@material-ui/core'
import clsx from 'clsx'

import { Dai } from 'components'

import useDaiChat from 'hooks/useDaiChat'
import useDaiChatPanelSize from 'hooks/useDaiChatPanelSize'

import styles, { PANEL_WIDTH } from './styles'

const CSS_VAR = '--dai-chat-panel-width'

const useStyles = makeStyles(styles)

const DaiChatPanel = ({ bannerVisible = false }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { chatOpen } = useDaiChat()
  const { panelWidth, setPanelWidth } = useDaiChatPanelSize()
  const panelRef = useRef(null)

  // Keep CSS var in sync with React state (open/close/resize commit)
  useEffect(() => {
    document.documentElement.style.setProperty(
      CSS_VAR,
      chatOpen && !isMobile ? `${panelWidth}px` : '0px',
    )
  }, [chatOpen, isMobile, panelWidth])

  // All resize logic lives in the mousedown handler via closure.
  // During drag we mutate the DOM and CSS var directly — zero React re-renders.
  // setPanelWidth is called exactly once on mouseup to sync context.
  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault()
      const panel = panelRef.current
      if (!panel) return

      panel.classList.add(classes.panelNoTransition)
      document.body.classList.add('dai-panel-resizing')
      let currentWidth = panel.getBoundingClientRect().width

      const onMouseMove = (moveEvent) => {
        const el = panelRef.current
        if (!el) return
        const newWidth = Math.max(
          PANEL_WIDTH,
          Math.min(
            el.getBoundingClientRect().right - moveEvent.clientX,
            window.innerWidth * 0.45,
          ),
        )
        currentWidth = newWidth
        el.style.width = `${newWidth}px`
        document.documentElement.style.setProperty(CSS_VAR, `${newWidth}px`)
      }

      const onMouseUp = () => {
        if (panelRef.current) {
          panelRef.current.classList.remove(classes.panelNoTransition)
        }
        document.body.classList.remove('dai-panel-resizing')
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        setPanelWidth(currentWidth)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [classes.panelNoTransition, setPanelWidth],
  )

  return (
    <Box
      ref={panelRef}
      className={clsx(classes.panel, {
        [classes.panelOpen]: chatOpen,
        [classes.panelWithBanner]: bannerVisible,
      })}
      style={chatOpen && !isMobile ? { width: panelWidth } : undefined}
    >
      {chatOpen && !isMobile && (
        <Box className={classes.resizeHandle} onMouseDown={handleResizeStart} />
      )}
      <Dai.Chat />
    </Box>
  )
}

export default memo(DaiChatPanel)
